import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Link,
  Chip,
  FormHelperText
} from '@mui/material';
import {
  Save as SaveIcon,
  PlayArrow as PlayArrowIcon,
  Refresh as RefreshIcon,
  History as HistoryIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import { useAuditLogPurgeService } from '../../services/auditLogPurgeService';
import type { AuditLogPurgeCronConfigDto, AuditLogPurgeRunDto, SpringPage } from '../../types/auditLogPurge';

const CRON_DOC_HREF =
  'https://docs.spring.io/spring-framework/docs/current/javadoc-api/org/springframework/scheduling/support/CronTrigger.html';

const formatTs = (iso: string | null | undefined) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('es-ES', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

const AuditLogPurgeSettingsTab: React.FC = () => {
  const { jwtToken } = useAuth();
  const auditLogPurgeService = useAuditLogPurgeService();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runsLoading, setRunsLoading] = useState(false);
  const [config, setConfig] = useState<AuditLogPurgeCronConfigDto | null>(null);
  const [enabled, setEnabled] = useState(true);
  const [cronExpression, setCronExpression] = useState('0 0 3 * * ?');
  const [retentionDays, setRetentionDays] = useState(14);
  const [globalError, setGlobalError] = useState<string | null>(null);
  const [runsError, setRunsError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [runsPage, setRunsPage] = useState<SpringPage<AuditLogPurgeRunDto> | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [confirmRunOpen, setConfirmRunOpen] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!jwtToken) {
      setGlobalError('No hay token de autenticación');
      setLoading(false);
      return;
    }
    setLoading(true);
    setGlobalError(null);
    const res = await auditLogPurgeService.getCronConfig();
    if (!res.ok) {
      if (res.status === 401 || res.status === 403) {
        setGlobalError('No tiene permiso para ver esta configuración (se requiere rol ADMIN en el backend).');
      } else {
        setGlobalError(res.error || 'No se pudo cargar la configuración');
      }
      setConfig(null);
    } else {
      setConfig(res.data);
      setEnabled(res.data.enabled);
      setCronExpression(res.data.cronExpression);
      setRetentionDays(res.data.retentionDays);
    }
    setLoading(false);
  }, [jwtToken]);

  const loadRuns = useCallback(async () => {
    if (!jwtToken) return;
    setRunsLoading(true);
    setRunsError(null);
    const res = await auditLogPurgeService.getRuns(page, rowsPerPage);
    if (!res.ok) {
      setRunsError(res.error || 'No se pudo cargar el historial');
      setRunsPage(null);
    } else {
      setRunsPage(res.data);
    }
    setRunsLoading(false);
  }, [jwtToken, page, rowsPerPage]);

  useEffect(() => {
    void loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    if (!loading && config) void loadRuns();
  }, [loading, config, loadRuns]);

  const handleSave = async () => {
    if (!jwtToken) return;
    setSaving(true);
    setFieldErrors({});
    setGlobalError(null);
    setSuccessMessage(null);
    const rd = Math.min(3650, Math.max(1, Math.floor(Number(retentionDays)) || 1));
    const res = await auditLogPurgeService.updateCronConfig({
      enabled,
      cronExpression: cronExpression.trim(),
      retentionDays: rd
    });
    if (!res.ok) {
      if (res.fieldErrors) setFieldErrors(res.fieldErrors);
      else setGlobalError(res.error || 'Error al guardar');
    } else {
      setConfig(res.data);
      setEnabled(res.data.enabled);
      setCronExpression(res.data.cronExpression);
      setRetentionDays(res.data.retentionDays);
      setSuccessMessage('Configuración guardada. El backend reprogramará la tarea con el nuevo cron.');
      void loadRuns();
    }
    setSaving(false);
  };

  const handleConfirmRun = async () => {
    if (!jwtToken) return;
    setConfirmRunOpen(false);
    setRunning(true);
    setGlobalError(null);
    setSuccessMessage(null);
    const res = await auditLogPurgeService.runManualPurge();
    if (!res.ok) {
      setGlobalError(res.error || 'Error al ejecutar la purga');
    } else {
      const r = res.data;
      setSuccessMessage(
        `Purga finalizada: estado ${r.status}. Filas eliminadas: ${r.rowsDeleted}. Corte (cutoff): ${formatTs(r.cutoffAt)}.`
      );
      setPage(0);
      setRunsLoading(true);
      setRunsError(null);
      const runsRes = await auditLogPurgeService.getRuns(0, rowsPerPage);
      if (!runsRes.ok) {
        setRunsError(runsRes.error || 'No se pudo actualizar el historial');
      } else {
        setRunsPage(runsRes.data);
      }
      setRunsLoading(false);
    }
    setRunning(false);
  };

  const handleChangePage = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (e: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {globalError && (
        <Alert severity="error" onClose={() => setGlobalError(null)}>
          {globalError}
        </Alert>
      )}
      {runsError && (
        <Alert severity="warning" onClose={() => setRunsError(null)}>
          {runsError}
        </Alert>
      )}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <ScheduleIcon sx={{ mr: 1 }} />
            Configuración programada
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Retención y cron se guardan en <code>audit_log_purge_cron_config</code>. Al guardar, el backend vuelve a programar el job.
          </Typography>
          {config && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
              Última actualización: {formatTs(config.updatedAt ?? undefined)} —{' '}
              {config.updatedByUsername ?? '—'}
            </Typography>
          )}
          <Stack spacing={2} sx={{ maxWidth: 560 }}>
            <FormControlLabel
              control={<Switch checked={enabled} onChange={(_, v) => setEnabled(v)} color="primary" />}
              label="Activar purga programada"
            />
            <TextField
              label="Expresión cron (Spring, 6 campos)"
              value={cronExpression}
              onChange={(e) => setCronExpression(e.target.value)}
              fullWidth
              required
              error={Boolean(fieldErrors.cronExpression)}
              helperText={
                fieldErrors.cronExpression ? (
                  fieldErrors.cronExpression
                ) : (
                  <span>
                    Formato: segundo, minuto, hora, día del mes, mes, día de semana. Ej.: todos los días a las 03:00 →{' '}
                    <code>0 0 3 * * ?</code>.{' '}
                    <Link href={CRON_DOC_HREF} target="_blank" rel="noopener noreferrer">
                      Documentación CronTrigger (Spring)
                    </Link>
                  </span>
                )
              }
            />
            <TextField
              label="Días de retención"
              type="number"
              value={retentionDays}
              onChange={(e) => setRetentionDays(Number(e.target.value))}
              fullWidth
              required
              inputProps={{ min: 1, max: 3650 }}
              error={Boolean(fieldErrors.retentionDays)}
              helperText={fieldErrors.retentionDays || 'Entre 1 y 3650 días (alineado con la API).'}
            />
            {fieldErrors.enabled && <FormHelperText error>{fieldErrors.enabled}</FormHelperText>}
            <Box>
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                onClick={() => void handleSave()}
                disabled={saving}
              >
                Guardar
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <PlayArrowIcon sx={{ mr: 1 }} />
            Ejecución manual
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Ejecuta la purga en este momento usando la retención actual en base de datos. Se eliminarán registros de
            auditoría con antigüedad mayor al período de retención.
          </Typography>
          <Button
            variant="outlined"
            color="warning"
            startIcon={running ? <CircularProgress size={18} /> : <PlayArrowIcon />}
            onClick={() => setConfirmRunOpen(true)}
            disabled={running}
          >
            Ejecutar purga ahora
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
              <HistoryIcon sx={{ mr: 1 }} />
              Historial de ejecuciones
            </Typography>
            <Button
              size="small"
              variant="outlined"
              startIcon={runsLoading ? <CircularProgress size={16} /> : <RefreshIcon />}
              onClick={() => void loadRuns()}
              disabled={runsLoading}
            >
              Actualizar
            </Button>
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Inicio</TableCell>
                  <TableCell>Fin</TableCell>
                  <TableCell align="right">Filas borradas</TableCell>
                  <TableCell align="right">Retención (días)</TableCell>
                  <TableCell>Disparador</TableCell>
                  <TableCell>Usuario</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runsLoading && !runsPage?.content?.length ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                      <CircularProgress size={28} />
                    </TableCell>
                  </TableRow>
                ) : runsPage?.content?.length ? (
                  runsPage.content.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>{formatTs(row.startedAt)}</TableCell>
                      <TableCell>{formatTs(row.finishedAt ?? undefined)}</TableCell>
                      <TableCell align="right">{row.rowsDeleted}</TableCell>
                      <TableCell align="right">{row.retentionDaysApplied}</TableCell>
                      <TableCell>
                        <Chip size="small" label={row.triggerType} variant="outlined" />
                      </TableCell>
                      <TableCell>{row.triggeredByUsername ?? '—'}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          color={row.status === 'SUCCESS' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell sx={{ maxWidth: 220, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {row.errorMessage ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No hay ejecuciones registradas
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          {runsPage && (
            <TablePagination
              component="div"
              count={runsPage.totalElements}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 20, 50]}
              labelRowsPerPage="Filas por página"
              labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`}
            />
          )}
        </CardContent>
      </Card>

      <Dialog open={confirmRunOpen} onClose={() => setConfirmRunOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirmar purga manual</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Se eliminarán de forma permanente los registros de auditoría más antiguos que el período de retención
            configurado en base de datos. Esta acción no se puede deshacer. ¿Desea continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRunOpen(false)}>Cancelar</Button>
          <Button onClick={() => void handleConfirmRun()} color="warning" variant="contained" autoFocus>
            Ejecutar purga
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default AuditLogPurgeSettingsTab;
